import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaSearch, FaBookmark, FaRegBookmark } from 'react-icons/fa';
import CandidateQuizDashboard from './CandidateQuizDashboard';
import GameHome from './game/GameHome';
import CandidateAttendance from './CandidateAttendance';
import CandidateRewardsDashboard from './CandidateRewardsDashboard';
import CandidateReferralDashboard from './CandidateReferralDashboard';
import CandidateSalaryAdvanceDashboard from './CandidateSalaryAdvanceDashboard';
import CandidateWorkDiary from './CandidateWorkDiary';
import CandidateCourses from './CandidateCourses';
import StaffingEmployeeHub from './StaffingEmployeeHub';
import { Button, Card, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;
const QUALIFICATIONS = ['10th pass', '12th pass', 'ITI', 'Diploma', 'Graduate'];

// Helper to get the full logo URL
const getLogoUrl = (logo_url) => {
  if (!logo_url) return '';
  if (logo_url.startsWith('http')) return logo_url;
  return `${API_URL}/uploads/logos/${logo_url}`;
};

function Dashboard({ onLogout }) {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('gogig_user')) || {});
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    name: user.name || '',
    email: user.email || '',
    qualification: user.qualification || '',
    course: user.course || '',
    alternate_mobile: user.alternate_mobile || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState('jobs'); // jobs | applied | saved | profile | quizzes | games | attendance | rewards | referrals | documents | salaryAdvance | higherEducation
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [applyStatus, setApplyStatus] = useState('');
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingApply, setLoadingApply] = useState(false);
  const [search, setSearch] = useState({ location: '', qualification: '', q: '' });
  const [toast, setToast] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [documents, setDocuments] = useState([]);
  const [isEmployed, setIsEmployed] = useState(user.isEmployed || false);
  const [docCategory, setDocCategory] = useState('naps');
  const [higherEducationOpen, setHigherEducationOpen] = useState(false);
  const [courseType, setCourseType] = useState('');
  const [myApplications, setMyApplications] = useState([]);
  const [workDiaryStatus, setWorkDiaryStatus] = useState({
    reminderDue: false,
    lastEntryDate: null,
    nextDueDate: null
  });
  const [workDiaryDiary, setWorkDiaryDiary] = useState(null);
  const [workDiaryLoading, setWorkDiaryLoading] = useState(false);
  const workDiaryReminderShown = useRef(false);
  const [candidateStatus, setCandidateStatus] = useState(null); // For supervisor-created candidates
  const [isSupervisorCreated, setIsSupervisorCreated] = useState(false);
  const [weeklyFeedback, setWeeklyFeedback] = useState(null); // { submitted, feedback, response_quote } or null
  const [weeklyFeedbackLoading, setWeeklyFeedbackLoading] = useState(false);
  const [weeklyFeedbackSubmitting, setWeeklyFeedbackSubmitting] = useState(false);
  const [visitedDatesThisWeek, setVisitedDatesThisWeek] = useState([]); // dates YYYY-MM-DD when user opened app this week (Mon–Sun)

  useEffect(() => {
    if (tab === 'jobs') fetchJobs();
    if (tab === 'applied') fetchAppliedJobs();
    if (tab === 'saved') fetchSavedJobs();
    if (tab === 'documents') {
      fetchDocuments();
      // Refresh candidate status when documents tab is opened
      if (isSupervisorCreated) {
        fetchCandidateProfile();
      }
    }
  }, [tab]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-dismiss toast after 2 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (user?.id) {
      fetchMyApplications();
    }
  }, [user]);

  const loadWorkDiary = useCallback(async (includeEntries = false) => {
    if (!isEmployed) return;
    const token = localStorage.getItem('gogig_token');
    if (!token) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      return;
    }
    const query = includeEntries ? '' : '?entries=false';
    try {
      if (includeEntries) setWorkDiaryLoading(true);
      const res = await fetch(`${API_URL}/api/candidate/work-diary${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setWorkDiaryStatus({
          reminderDue: !!data.reminderDue,
          lastEntryDate: data.lastEntryDate || null,
          nextDueDate: data.nextDueDate || null
        });
        setWorkDiaryDiary(prev => {
          const diary = data.diary || null;
          const withSuggestions = diary || data.suggestedDetails ? { ...diary, _suggestedDetails: data.suggestedDetails || null } : diary;
          return (includeEntries || !prev ? withSuggestions : prev);
        });
        if (data.reminderDue && !workDiaryReminderShown.current) {
          setToast('It has been a week since your last work diary entry. Please update it.');
          workDiaryReminderShown.current = true;
        }
      }
    } catch (err) {
      console.error('Error fetching work diary:', err);
    } finally {
      if (includeEntries) setWorkDiaryLoading(false);
    }
  }, [isEmployed]);

  const fetchJobs = async () => {
    setLoadingJobs(true);
    let url = `${API_URL}/api/candidate/jobs?`;
    if (search.location) url += `location=${encodeURIComponent(search.location)}&`;
    if (search.qualification) url += `qualification=${encodeURIComponent(search.qualification)}&`;
    if (search.q) url += `q=${encodeURIComponent(search.q)}&`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.success) setJobs(data.jobs);
    setLoadingJobs(false);
  };

  const fetchAppliedJobs = async () => {
    const token = localStorage.getItem('gogig_token');
    if (!token) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      return;
    }
    const res = await fetch(`${API_URL}/api/candidate/applications`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    if (data.success) setAppliedJobs(data.applications);
  };

  const fetchSavedJobs = async () => {
    const token = localStorage.getItem('gogig_token');
    if (!token) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      return;
    }
    const res = await fetch(`${API_URL}/api/candidate/bookmarks`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    if (data.success) setSavedJobs(data.bookmarks);
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('gogig_token');
      if (!token) {
        alert('Session expired. Please log in again.');
        window.location.href = '/welcome';
        return;
      }
      const res = await fetch(`${API_URL}/api/documents/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setDocuments(data.documents || []);
    } catch (err) {
      setDocuments([]);
    }
  };

  const handleApply = async job => {
    setLoadingApply(true);
    setApplyStatus('');
    const token = localStorage.getItem('gogig_token');
    if (!token) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      setLoadingApply(false);
      return;
    }
    const res = await fetch(`${API_URL}/api/candidate/jobs/${job.id}/apply`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    if (res.ok) setApplyStatus('Applied!');
    else setApplyStatus(data.error || 'Failed to apply');
    setLoadingApply(false);
    fetchAppliedJobs();
    setToast(res.ok ? 'Applied to job!' : (data.error || 'Failed to apply'));
  };

  const handleBookmark = async (job, isBookmarked) => {
    const token = localStorage.getItem('gogig_token');
    if (!token) {
      alert('Session expired. Please log in again.');
      window.location.href = '/welcome';
      return;
    }
    if (isBookmarked) {
      await fetch(`${API_URL}/api/candidate/jobs/${job.id}/bookmark`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setToast('Removed from saved jobs');
    } else {
      await fetch(`${API_URL}/api/candidate/jobs/${job.id}/bookmark`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setToast('Saved job!');
    }
    fetchSavedJobs();
  };

  const isBookmarked = job => savedJobs.some(j => j.id === job.id);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const token = localStorage.getItem('gogig_token');
      if (!token) {
        alert('Session expired. Please log in again.');
        window.location.href = '/welcome';
        setLoading(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/candidate/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setUser({ ...user, ...form });
      localStorage.setItem('gogig_user', JSON.stringify({ ...user, ...form }));
      setSuccess('Profile updated!');
      setEdit(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmploymentStatus = async () => {
    try {
      console.log('Fetching employment status...');
      const token = localStorage.getItem('gogig_token');
      if (!token) {
        alert('Session expired. Please log in again.');
        window.location.href = '/welcome';
        return;
      }
      const res = await fetch(`${API_URL}/api/candidate/employment-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      console.log('Employment status response:', data);
      
      if (data.success) {
        console.log('Setting employment status to:', data.isEmployed);
        setIsEmployed(data.isEmployed);
        
        // Update default tab based on employment status
        if (data.isEmployed && ['jobs', 'applied', 'saved'].includes(tab)) {
          console.log('Employed user, redirecting to documents tab');
          setTab('documents');
        } else if (!data.isEmployed && ['documents', 'games', 'quizzes', 'attendance'].includes(tab)) {
          console.log('Unemployed user, redirecting to jobs tab');
          setTab('jobs');
        }
      }
    } catch (err) {
      console.error('Error fetching employment status:', err);
      setIsEmployed(false);
      if (['documents', 'games', 'quizzes', 'attendance'].includes(tab)) {
        setTab('jobs');
      }
    }
  };

  // Fetch candidate profile to check if created by supervisor
  const fetchCandidateProfile = async () => {
    try {
      const token = localStorage.getItem('gogig_token');
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/candidate/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success && data.profile) {
        const profile = data.profile;
        const isSupervisorCreated = profile.created_by_supervisor !== null && profile.created_by_supervisor !== undefined;
        setIsSupervisorCreated(isSupervisorCreated);
        
        if (isSupervisorCreated) {
          // Get status from profile (could be in status field or additional_details)
          const status = profile.status || profile.contract_status || null;
          setCandidateStatus(status);
        } else {
          setCandidateStatus(null);
        }
      }
    } catch (err) {
      console.error('Error fetching candidate profile:', err);
    }
  };

  // Add this useEffect to check employment status when component mounts
  useEffect(() => {
    fetchEmploymentStatus();
    fetchCandidateProfile();
  }, []); // Remove the interval to avoid potential race conditions

  // Add this useEffect to handle tab changes based on employment status
  useEffect(() => {
    if (isEmployed && ['jobs', 'applied', 'saved'].includes(tab)) {
      setTab('documents');
    } else if (!isEmployed && ['documents', 'games', 'quizzes', 'attendance'].includes(tab)) {
      setTab('jobs');
    }
    
    // Refresh candidate profile when employment status changes (for supervisor-created candidates)
    if (isSupervisorCreated) {
      fetchCandidateProfile();
    }
  }, [isEmployed, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEmployed) {
      loadWorkDiary(false);
    }
  }, [isEmployed, loadWorkDiary]);

  const fetchWeeklyFeedback = useCallback(async () => {
    if (!isEmployed) return;
    const token = localStorage.getItem('gogig_token');
    if (!token) return;
    setWeeklyFeedbackLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/candidate/weekly-feedback/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setWeeklyFeedback({
          submitted: data.submitted,
          feedback: data.feedback,
          response_quote: data.response_quote || null
        });
      } else if (!res.ok) {
        setToast(data.message || 'Could not load weekly feedback.');
      }
    } catch (err) {
      console.error('Error fetching weekly feedback:', err);
      setToast('Could not load weekly feedback.');
    } finally {
      setWeeklyFeedbackLoading(false);
    }
  }, [isEmployed]);

  useEffect(() => {
    if (isEmployed) fetchWeeklyFeedback();
  }, [isEmployed, fetchWeeklyFeedback]);

  const fetchDailyVisits = useCallback(async () => {
    if (!isEmployed) return;
    const token = localStorage.getItem('gogig_token');
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/candidate/daily-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source: 'web' })
      });
      const res = await fetch(`${API_URL}/api/candidate/daily-visits/current-week`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (data.success && Array.isArray(data.visited_dates)) setVisitedDatesThisWeek(data.visited_dates);
    } catch (err) {
      console.error('Error fetching daily visits:', err);
    }
  }, [isEmployed]);

  useEffect(() => {
    if (isEmployed) fetchDailyVisits();
  }, [isEmployed, fetchDailyVisits]);

  const WEEKLY_FEEDBACK_QUOTES = {
    great: "🔥 Amazing! Keep shining this week!",
    good: "👍 Good job! Next week let's aim even higher.",
    okay: "💪 Every week is a chance to grow.",
    tough: "🌱 Tough weeks build strong people. You got this!"
  };

  const submitWeeklyFeedback = async (feedback) => {
    const token = localStorage.getItem('gogig_token');
    if (!token) {
      setToast('Please sign in again.');
      return;
    }
    setWeeklyFeedbackSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/candidate/weekly-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ feedback })
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        const quote = data.response_quote || WEEKLY_FEEDBACK_QUOTES[feedback] || 'Thank you for your feedback!';
        setWeeklyFeedback({ submitted: true, feedback: data.feedback, response_quote: quote });
      } else {
        setToast(data.message || 'Failed to submit. Try again.');
      }
    } catch (err) {
      console.error('Error submitting weekly feedback:', err);
      setToast('Failed to submit. Try again.');
    } finally {
      setWeeklyFeedbackSubmitting(false);
    }
  };

  useEffect(() => {
    if (tab === 'workDiary' && isEmployed) {
      loadWorkDiary(true);
    }
  }, [tab, isEmployed, loadWorkDiary]);

  const fetchMyApplications = async () => {
    try {
      const token = localStorage.getItem('gogig_token');
      if (!token) {
        alert('Session expired. Please log in again.');
        window.location.href = '/welcome';
        return;
      }
      const res = await fetch(`${API_URL}/api/candidate/higher-education/my-applications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMyApplications(data.applications);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    }
  };

  const handleHigherEducationSubmit = async () => {
    try {
      const token = localStorage.getItem('gogig_token');
      if (!token) {
        alert('Session expired. Please log in again.');
        window.location.href = '/welcome';
        return;
      }
      const res = await fetch(`${API_URL}/api/candidate/higher-education/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ course_type: courseType })
      });
      const data = await res.json();
      if (data.success) {
        setHigherEducationOpen(false);
        setCourseType('');
        fetchMyApplications();
        alert('Application submitted successfully!');
      } else {
        alert(data.message || 'Failed to submit application');
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      alert('Failed to submit application');
    }
  };

  // Determine notification message based on status
  const getNotificationMessage = () => {
    if (!isSupervisorCreated) return null;
    
    // Normalize status for comparison
    const normalizedStatus = candidateStatus ? candidateStatus.toLowerCase().trim() : '';
    
    // Show documents_pending message
    if (normalizedStatus === 'documents_pending') {
      return {
        message: 'Your documents are pending. Please submit your documents to your supervisor to move further.',
        type: 'warning',
        color: '#f59e0b'
      };
    }
    
    // Show contract not generated message
    if (normalizedStatus === 'contract not generated' || (!isEmployed && normalizedStatus !== 'documents_pending')) {
      return {
        message: 'Please wait for your contract to get generated so that you will be moved to an employed view.',
        type: 'info',
        color: '#3b82f6'
      };
    }
    
    // If employed, don't show notification (contract is generated)
    if (isEmployed) {
      return null;
    }
    
    // Default: show contract not generated message for supervisor-created candidates
    return {
      message: 'Please wait for your contract to get generated so that you will be moved to an employed view.',
      type: 'info',
      color: '#3b82f6'
    };
  };

  const notification = getNotificationMessage();

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {toast && <div style={{ position: 'fixed', top: 24, right: 24, background: '#22c55e', color: '#fff', padding: '12px 24px', borderRadius: 8, zIndex: 1001 }}>{toast}</div>}
      
      {/* Supervisor Candidate Status Notification Banner */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: notification.color,
          color: '#fff',
          padding: '16px 24px',
          textAlign: 'center',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          fontSize: windowWidth < 768 ? 14 : 16,
          fontWeight: 500,
          lineHeight: 1.5
        }}>
          {notification.message}
        </div>
      )}
      
      <div style={{ 
        background: '#fff', 
        borderRadius: 18, 
        boxShadow: '0 2px 16px rgba(34,197,94,0.07)', 
        padding: 36, 
        minWidth: 340, 
        textAlign: 'center', 
        border: '1.5px solid #e5e7eb', 
        maxWidth: 700, 
        width: '100%',
        marginTop: notification ? '60px' : '0',
        transition: 'margin-top 0.3s ease'
      }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
          {/* Not Employed Candidate Tabs */}
          {!isEmployed && (
            <>
              <button className={`gogig-card-btn${tab === 'jobs' ? '' : '-outline'}`} onClick={() => setTab('jobs')}>Browse Jobs</button>
              <button className={`gogig-card-btn${tab === 'applied' ? '' : '-outline'}`} onClick={() => setTab('applied')}>Applied Jobs</button>
              <button className={`gogig-card-btn${tab === 'saved' ? '' : '-outline'}`} onClick={() => setTab('saved')}>Saved Jobs</button>
              <button className={`gogig-card-btn${tab === 'profile' ? '' : '-outline'}`} onClick={() => setTab('profile')}>Profile</button>
              <button className={`gogig-card-btn${tab === 'referrals' ? '' : '-outline'}`} onClick={() => setTab('referrals')}>Referrals</button>
              <button className={`gogig-card-btn${tab === 'rewards' ? '' : '-outline'}`} onClick={() => setTab('rewards')}>Rewards</button>
              <button className={`gogig-card-btn${tab === 'higherEducation' ? '' : '-outline'}`} onClick={() => setTab('higherEducation')}>Higher Education</button>
              <button className={`gogig-card-btn${tab === 'courses' ? '' : '-outline'}`} onClick={() => setTab('courses')}>Courses</button>
            </>
          )}
          
          {/* Employed Candidate Tabs */}
          {isEmployed && (
            <>
              <button className={`gogig-card-btn${tab === 'documents' ? '' : '-outline'}`} onClick={() => setTab('documents')}>Documents</button>
              <button className={`gogig-card-btn${tab === 'workDiary' ? '' : '-outline'}`} onClick={() => setTab('workDiary')}>Work Diary</button>
              <button className={`gogig-card-btn${tab === 'games' ? '' : '-outline'}`} onClick={() => setTab('games')}>Games</button>
              <button className={`gogig-card-btn${tab === 'quizzes' ? '' : '-outline'}`} onClick={() => setTab('quizzes')}>Quizzes</button>
              <button className={`gogig-card-btn${tab === 'attendance' ? '' : '-outline'}`} onClick={() => setTab('attendance')}>Attendance</button>
              <button className={`gogig-card-btn${tab === 'referrals' ? '' : '-outline'}`} onClick={() => setTab('referrals')}>Referrals</button>
              <button className={`gogig-card-btn${tab === 'rewards' ? '' : '-outline'}`} onClick={() => setTab('rewards')}>Rewards</button>
              <button className={`gogig-card-btn${tab === 'salaryAdvance' ? '' : '-outline'}`} onClick={() => setTab('salaryAdvance')}>Salary Advance</button>
              <button className={`gogig-card-btn${tab === 'profile' ? '' : '-outline'}`} onClick={() => setTab('profile')}>Profile</button>
              <button className={`gogig-card-btn${tab === 'higherEducation' ? '' : '-outline'}`} onClick={() => setTab('higherEducation')}>Higher Education</button>
              <button className={`gogig-card-btn${tab === 'courses' ? '' : '-outline'}`} onClick={() => setTab('courses')}>Courses</button>
            </>
          )}
        </div>

        <StaffingEmployeeHub
          token={localStorage.getItem('gogig_token')}
          onGoAttendance={() => setTab('attendance')}
        />

        {/* Daily app visits banner: Mon–Sun ticks when user opens app (employed only) */}
        {isEmployed && (() => {
          const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
          const now = new Date();
          const day = now.getDay();
          const toMonday = day === 0 ? -6 : 1 - day;
          const monday = new Date(now);
          monday.setDate(now.getDate() + toMonday);
          monday.setHours(0, 0, 0, 0);
          const weekDates = [];
          for (let i = 0; i < 7; i++) {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            weekDates.push(d.toISOString().slice(0, 10));
          }
          const visitedSet = new Set(visitedDatesThisWeek || []);
          return (
            <div style={{
              marginBottom: 20,
              padding: 20,
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              borderRadius: 16,
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)',
              maxWidth: 700,
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔥</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>💪 Great Workers Show Up Every Day</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.95)' }}>Open the app each day to fill your week!</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                {days.map((label, i) => {
                  const checked = visitedSet.has(weekDates[i]);
                  return (
                    <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{label}</span>
                      <div style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: checked ? '#fff' : 'rgba(255,255,255,0.2)',
                        border: '1.5px solid #fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        color: checked ? '#6366f1' : 'transparent'
                      }}>
                        {checked ? '✓' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Weekly feedback card (employed only) */}
        {isEmployed && (
          <div style={{
            marginBottom: 20,
            padding: 20,
            background: '#fff',
            borderRadius: 16,
            border: '1.5px solid #e5e7eb',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            maxWidth: 700,
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>How was your week?</Typography>
            {weeklyFeedbackLoading ? (
              <div style={{ padding: '12px 0', color: '#64748b' }}>Loading...</div>
            ) : weeklyFeedback && weeklyFeedback.submitted && weeklyFeedback.response_quote ? (
              <div style={{ padding: '8px 0', fontSize: 16, color: '#334155', fontStyle: 'italic' }}>"{weeklyFeedback.response_quote}"</div>
            ) : (
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {[
                  { key: 'great', emoji: '😃', label: 'Great' },
                  { key: 'good', emoji: '🙂', label: 'Good' },
                  { key: 'okay', emoji: '😐', label: 'Okay' },
                  { key: 'tough', emoji: '😓', label: 'Tough' }
                ].map(({ key, emoji, label }) => (
                  <button
                    key={key}
                    type="button"
                    disabled={weeklyFeedbackSubmitting}
                    onClick={() => submitWeeklyFeedback(key)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      padding: '12px 16px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: 12,
                      background: '#fff',
                      cursor: weeklyFeedbackSubmitting ? 'not-allowed' : 'pointer',
                      fontSize: 24,
                      opacity: weeklyFeedbackSubmitting ? 0.7 : 1
                    }}
                  >
                    <span>{emoji}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'courses' && (
          <div style={{ width: '100%' }}>
            <CandidateCourses />
          </div>
        )}
        {tab === 'jobs' && (
          <div>
            <div style={{
              display: 'flex',
              flexWrap: windowWidth < 600 ? 'wrap' : 'nowrap',
              gap: 14,
              marginBottom: 22,
              alignItems: 'center',
              background: 'rgba(248,250,252,0.95)',
              borderRadius: 16,
              border: '1.5px solid #e5e7eb',
              boxShadow: '0 2px 12px rgba(34,197,94,0.07)',
              padding: windowWidth < 600 ? 10 : 18,
              maxWidth: 700,
              width: '100%',
              boxSizing: 'border-box',
              flexDirection: windowWidth < 600 ? 'column' : 'row',
              transition: 'all 0.2s'
            }}>
              <div style={{ position: 'relative', flex: '1 1 0', minWidth: 120, display: 'flex', alignItems: 'center' }}>
                <FaSearch style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#bdbdbd', pointerEvents: 'none', fontSize: 22 }} />
                <input
                  placeholder="Search jobs..."
                  value={search.q}
                  onChange={e => setSearch(s => ({ ...s, q: e.target.value }))}
                  className="gogig-input searchbar-input"
                  style={{
                    paddingLeft: 44,
                    border: 'none',
                    borderRadius: 12,
                    background: '#fff',
                    height: 46,
                    fontSize: 17,
                    width: '100%',
                    minWidth: 100,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                    outline: 'none',
                    color: '#222',
                    transition: 'box-shadow 0.2s'
                  }}
                />
              </div>
              <input
                placeholder="Location"
                value={search.location}
                onChange={e => setSearch(s => ({ ...s, location: e.target.value }))}
                className="gogig-input searchbar-input"
                style={{
                  border: 'none',
                  borderRadius: 12,
                  background: '#fff',
                  height: 46,
                  fontSize: 17,
                  flex: '1 1 0',
                  minWidth: 100,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                  outline: 'none',
                  color: '#222',
                  paddingLeft: 18,
                  transition: 'box-shadow 0.2s'
                }}
              />
              <select
                value={search.qualification}
                onChange={e => setSearch(s => ({ ...s, qualification: e.target.value }))}
                className="gogig-input searchbar-input"
                style={{
                  border: 'none',
                  borderRadius: 12,
                  background: '#fff',
                  height: 46,
                  fontSize: 17,
                  flex: '1 1 0',
                  minWidth: 120,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                  outline: 'none',
                  color: '#222',
                  paddingLeft: 18,
                  transition: 'box-shadow 0.2s'
                }}
              >
                <option value="">All Qualifications</option>
                {QUALIFICATIONS.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
              <button
                className="gogig-card-btn searchbar-btn"
                onClick={fetchJobs}
                style={{
                  borderRadius: 12,
                  height: 46,
                  fontSize: 18,
                  width: 120,
                  minWidth: 90,
                  fontWeight: 700,
                  background: '#22c55e',
                  color: '#fff',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(34,197,94,0.10)',
                  cursor: 'pointer',
                  transition: 'background 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#16a34a'}
                onMouseOut={e => e.currentTarget.style.background = '#22c55e'}
              >Search</button>
            </div>
            <style>{`
              .searchbar-input::placeholder {
                color: #bdbdbd;
                opacity: 1;
                font-size: 16px;
              }
              .searchbar-input:focus {
                box-shadow: 0 2px 8px rgba(34,197,94,0.10);
                border: 1.5px solid #22c55e;
              }
              @media (max-width: 600px) {
                .searchbar-input, .searchbar-btn {
                  width: 100% !important;
                  min-width: 0 !important;
                  margin-bottom: 8px;
                }
              }
            `}</style>
            {loadingJobs ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                <div className="spinner" style={{ width: 36, height: 36, border: '4px solid #e5e7eb', borderTop: '4px solid #22c55e', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
              </div>
            ) : jobs.length === 0 ? <div style={{ color: '#888' }}>No jobs found.</div> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
                {jobs.map(job => (
                  <div key={job.id} style={{ boxShadow: '0 2px 12px rgba(34,197,94,0.08)', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: 20, background: '#fff', cursor: 'pointer', position: 'relative', transition: 'box-shadow 0.2s', minHeight: 180 }} onClick={() => setSelectedJob(job)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                      <img
                        src={job.logo_url ? getLogoUrl(job.logo_url) : 'https://via.placeholder.com/48?text=Logo'}
                        alt="Logo"
                        style={{ width: 48, height: 48, borderRadius: 12, background: '#f3f3f3', objectFit: 'cover' }}
                      />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>{job.company_name}</div>
                        <div style={{ color: '#555', fontSize: 15, fontWeight: 600 }}>{job.title}</div>
                      </div>
                    </div>
                    <div style={{ color: '#888', fontSize: 14, marginBottom: 6 }}>Location: {job.location} | Stipend: ₹{job.stipend_min} - ₹{job.stipend_max}</div>
                    <div style={{ color: '#888', fontSize: 14, marginBottom: 6 }}>Qualification: {job.qualification}</div>
                    <div style={{ color: '#444', fontSize: 14, marginBottom: 8, minHeight: 32, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} dangerouslySetInnerHTML={{ __html: (job.details || '').replace(/<[^>]+>/g, '').slice(0, 100) + (job.details && job.details.length > 100 ? '...' : '') }} />
                    <div style={{ position: 'absolute', top: 18, right: 18, zIndex: 2 }} onClick={e => { e.stopPropagation(); handleBookmark(job, isBookmarked(job)); }}>
                      {isBookmarked(job) ? <FaBookmark color="#22c55e" size={22} /> : <FaRegBookmark color="#aaa" size={22} />}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Job Details Modal */}
            {selectedJob && (
              <div className="gogig-modal-bg" onClick={() => setSelectedJob(null)}>
                <div className="gogig-modal" style={{ minWidth: 340, maxWidth: 600, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                    <img
                      src={selectedJob.logo_url ? getLogoUrl(selectedJob.logo_url) : 'https://via.placeholder.com/48?text=Logo'}
                      alt="Logo"
                      style={{ width: 48, height: 48, borderRadius: 12, background: '#f3f3f3', objectFit: 'cover' }}
                    />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 18 }}>{selectedJob.company_name}</div>
                      <div style={{ color: '#555', fontSize: 15 }}>{selectedJob.title}</div>
                    </div>
                  </div>
                  <div style={{ color: '#888', fontSize: 14, marginBottom: 8 }}>Location: {selectedJob.location} | Age: {selectedJob.age_criteria} | Stipend: ₹{selectedJob.stipend_min} - ₹{selectedJob.stipend_max}</div>
                  <div style={{ color: '#888', fontSize: 14, marginBottom: 8 }}>Qualification: {selectedJob.qualification}</div>
                  <div style={{ margin: '16px 0', color: '#222', fontSize: 15 }} dangerouslySetInnerHTML={{ __html: selectedJob.details }} />
                  <button className="gogig-card-btn" style={{ marginTop: 10 }} onClick={() => handleApply(selectedJob)} disabled={loadingApply}>{loadingApply ? 'Applying...' : 'Apply'}</button>
                  {applyStatus && <div style={{ color: applyStatus === 'Applied!' ? 'green' : 'red', marginTop: 8 }}>{applyStatus}</div>}
                  <button className="gogig-card-btn-outline" style={{ marginTop: 10 }} onClick={() => setSelectedJob(null)}>Close</button>
                </div>
              </div>
            )}
          </div>
        )}
        {tab === 'applied' && (
          <div>
            {appliedJobs.length === 0 ? <div style={{ color: '#888' }}>No applied jobs yet.</div> : appliedJobs.map(app => (
              <div key={app.id} style={{ border: '1.5px solid #e5e7eb', borderRadius: 12, marginBottom: 18, padding: 18, background: '#f9f9ff', textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                  <img
                    src={app.logo_url ? getLogoUrl(app.logo_url) : 'https://via.placeholder.com/48?text=Logo'}
                    alt="Logo"
                    style={{ width: 48, height: 48, borderRadius: 8, background: '#f3f3f3' }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 18 }}>{app.company_name}</div>
                    <div style={{ color: '#555', fontSize: 15 }}>{app.title}</div>
                  </div>
                </div>
                <div style={{ color: '#888', fontSize: 14 }}>Location: {app.location} | Stipend: ₹{app.stipend_min} - ₹{app.stipend_max} | Qualification: {app.qualification}</div>
                <div style={{ color: '#888', fontSize: 14, marginTop: 4 }}>Status: <b>{app.status}</b></div>
              </div>
            ))}
          </div>
        )}
        {tab === 'saved' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {savedJobs.length === 0 ? <div style={{ color: '#888' }}>No saved jobs yet.</div> : savedJobs.map(job => (
              <div key={job.id} style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px', fontSize: 15, color: '#222', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img
                    src={job.logo_url ? getLogoUrl(job.logo_url) : 'https://via.placeholder.com/48?text=Logo'}
                    alt="Logo"
                    style={{ width: 36, height: 36, borderRadius: 8, background: '#f3f3f3', objectFit: 'cover' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{job.title}</div>
                    <div style={{ color: '#888', fontSize: 13 }}>Location: {job.location} | ₹{job.stipend_min}-{job.stipend_max} | {job.qualification}</div>
                  </div>
                </div>
                <FaBookmark color="#22c55e" size={20} title="Unsave" style={{ cursor: 'pointer' }} onClick={() => handleBookmark(job, true)} />
              </div>
            ))}
          </div>
        )}
        {tab === 'profile' && (
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
            <input name="name" value={form.name} onChange={handleChange} className="gogig-input" placeholder="Name" />
            <input name="email" value={form.email} onChange={handleChange} className="gogig-input" placeholder="Email" />
            <input name="qualification" value={form.qualification} onChange={handleChange} className="gogig-input" placeholder="Qualification" />
            <input name="course" value={form.course} onChange={handleChange} className="gogig-input" placeholder="Course" />
            <input name="alternate_mobile" value={form.alternate_mobile} onChange={handleChange} className="gogig-input" placeholder="Alternate Mobile Number (optional)" maxLength={10} pattern="[0-9]{10}" />
            {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
            {success && <div style={{ color: 'green', marginBottom: 8 }}>{success}</div>}
            <button className="gogig-card-btn" type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
            <button className="gogig-card-btn-outline" type="button" onClick={() => setEdit(false)}>Cancel</button>
          </form>
        )}
        {tab === 'referrals' && (
          <div style={{ width: '100%' }}>
            <CandidateReferralDashboard candidateId={user.id} />
          </div>
        )}
        {tab === 'rewards' && (
          <div style={{ width: '100%' }}>
            <CandidateRewardsDashboard candidateId={user.id} />
          </div>
        )}
        {tab === 'salaryAdvance' && (
          <div style={{ width: '100%' }}>
            <CandidateSalaryAdvanceDashboard candidateId={user.id} />
          </div>
        )}
        {tab === 'higherEducation' && (
          <>
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Higher Education
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => setHigherEducationOpen(true)}
                sx={{ mb: 2 }}
              >
                Apply for Higher Education
              </Button>
              {myApplications.length > 0 && (
                <div>
                  <Typography variant="subtitle1" gutterBottom>
                    My Applications
                  </Typography>
                  {myApplications.map(app => (
                    <Card key={app.id} sx={{ p: 2, mb: 2 }}>
                      <Typography variant="body1">
                        Course: {app.course_type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Applied on: {new Date(app.created_at).toLocaleDateString()}
                      </Typography>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
            <Dialog open={higherEducationOpen} onClose={() => setHigherEducationOpen(false)}>
              <DialogTitle>Apply for Higher Education</DialogTitle>
              <DialogContent>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Course Type</InputLabel>
                  <Select
                    value={courseType}
                    label="Course Type"
                    onChange={(e) => setCourseType(e.target.value)}
                  >
                    <MenuItem value="Diploma">Diploma Course</MenuItem>
                    <MenuItem value="Graduate">Graduate Course</MenuItem>
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setHigherEducationOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleHigherEducationSubmit}
                  disabled={!courseType}
                  variant="contained"
                >
                  Submit
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        {tab === 'documents' && (
          <div>
            {documents.length === 0 ? (
              <div style={{ color: '#888', marginTop: 16 }}>No documents found.</div>
            ) : (
              <div style={{ marginTop: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px #e5e7eb', overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6' }}>
                      <th style={{ padding: '10px 8px', fontWeight: 700, fontSize: 15, color: '#222', borderBottom: '1.5px solid #e5e7eb' }}>Type</th>
                      <th style={{ padding: '10px 8px', fontWeight: 700, fontSize: 15, color: '#222', borderBottom: '1.5px solid #e5e7eb' }}>Date</th>
                      <th style={{ padding: '10px 8px', fontWeight: 700, fontSize: 15, color: '#222', borderBottom: '1.5px solid #e5e7eb' }}>Company</th>
                      <th style={{ padding: '10px 8px', fontWeight: 700, fontSize: 15, color: '#222', borderBottom: '1.5px solid #e5e7eb' }}>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map(doc => {
                      // Find the best date field
                      const dateRaw = doc.IssueDate || doc['Issue-date'] || doc.date || doc.created_at || null;
                      let date = 'No Date';
                      if (dateRaw) {
                        try {
                          const d = new Date(dateRaw);
                          date = !isNaN(d) ? d.toISOString().slice(0,10) : dateRaw;
                        } catch { date = dateRaw; }
                      }
                      // Find the best company field
                      const company = doc.company_name || doc.EstablishmentName || doc.company || doc.employer || '-';
                      // Download link: use /contract/:id/pdf for contract, else /:id/pdf
                      const pdfUrl = doc.document_type === 'contract'
                        ? `${API_URL}/api/documents/contract/${doc.id}/pdf?token=${localStorage.getItem('gogig_token')}`
                        : `${API_URL}/api/documents/${doc.id}/pdf?token=${localStorage.getItem('gogig_token')}`;
                      return (
                        <tr key={doc.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 8px', fontWeight: 600, color: '#222', textTransform: 'capitalize' }}>{doc.document_type || doc.category || 'Document'}</td>
                          <td style={{ padding: '10px 8px', color: '#444' }}>{date}</td>
                          <td style={{ padding: '10px 8px', color: '#444' }}>{company}</td>
                          <td style={{ padding: '10px 8px' }}>
                            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#22c55e', fontWeight: 700, textDecoration: 'none', border: '1.5px solid #22c55e', borderRadius: 6, padding: '6px 14px', transition: 'background 0.2s', background: '#f8fafc' }}>
                              Download PDF
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {tab === 'workDiary' && (
          <div style={{ width: '100%' }}>
            <CandidateWorkDiary
              apiUrl={API_URL}
              diary={workDiaryDiary}
              status={workDiaryStatus}
              loading={workDiaryLoading}
              onToast={(message) => setToast(message)}
              onRefresh={() => loadWorkDiary(true)}
              candidateName={user.name}
            />
          </div>
        )}
        {tab === 'games' && (
          <div>
            <GameHome user={user} onBack={() => setTab('documents')} />
          </div>
        )}
        {tab === 'quizzes' && (
          <div>
            <CandidateQuizDashboard candidateId={user.id} />
          </div>
        )}
        {tab === 'attendance' && (
          <div>
            <CandidateAttendance candidateId={user.id} />
          </div>
        )}
        {tab === 'referrals' && (
          <div>
            <CandidateReferralDashboard candidateId={user.id} />
          </div>
        )}
        <button className="gogig-card-btn-outline" style={{ marginTop: 24 }} onClick={onLogout}>Logout</button>
      </div>
    </div>
  );
}

export default Dashboard; 

